import { fetchJSON as fetchAPI } from './client';

export type FinancialPlan = {
  id: string;
  name: string;
  status: string;
  revenue_forecast: number;
  gross_margin: number;
  operating_margin: number;
  budget_amount: number;
  planned_spend: number;
  budget_variance: number;
  net_cash_flow: number;
  working_capital: number;
  cash_conversion_cycle: number;
  health_score: number;
  roi: number;
};

export type Budget = {
  id: string;
  name: string;
  budget_type: string;
  department?: string;
  total_amount: number;
  allocated: number;
  remaining: number;
  utilization_pct: number;
  fiscal_period: string;
};

export type CashFlowPlan = {
  id: string;
  period: string;
  cash_inflow: number;
  cash_outflow: number;
  net_cash_flow: number;
  supplier_payments: number;
  customer_collections: number;
  inventory_investment: number;
  cash_buffer: number;
};

export type ProfitabilityModel = {
  id: string;
  dimension: string;
  dimension_name: string;
  revenue: number;
  gross_profit: number;
  contribution_margin: number;
  operating_margin: number;
  profitability_score: number;
};

export type WorkingCapitalModel = {
  inventory_investment: number;
  receivables: number;
  payables: number;
  working_capital: number;
  cash_conversion_cycle: number;
  dio: number;
  dso: number;
  dpo: number;
  utilization_pct: number;
  optimization_note?: string;
};

export type FinancialRisk = {
  id: string;
  risk_type: string;
  severity: string;
  probability: number;
  business_impact: string;
  recommended_mitigation: string;
};

export type FinancialRecommendation = {
  id: string;
  type: string;
  title: string;
  description: string;
  business_explanation: string;
  confidence: number;
  expected_roi: number;
  expected_savings: number;
  margin_impact_pct?: number;
  cash_impact?: number;
  auto_execute: boolean;
  status: string;
};

export type FinancialScenario = {
  id: string;
  name: string;
  scenario_type: string;
  revenue: number;
  gross_margin: number;
  cash_flow: number;
  working_capital: number;
  roi: number;
  enterprise_value: number;
  description?: string;
};

export type FinancialDashboard = {
  active_plans: number;
  revenue_forecast: number;
  revenue_growth: number;
  gross_margin: number;
  operating_margin: number;
  working_capital: number;
  cash_conversion_cycle: number;
  budget_variance: number;
  net_cash_flow: number;
  roi: number;
  health_score: number;
  open_risks: number;
  pending_recommendations: number;
  forecast_accuracy: number;
};

const API_BASE = import.meta.env.VITE_FINANCIALPLANNING_API ?? '/api/v1';

function finFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const financialPlanningApi = {
  dashboard: () => finFetch<FinancialDashboard>('/financial-dashboard'),
  listPlans: () => finFetch<FinancialPlan[]>('/financial-plans'),
  listBudgets: (planId?: string) => finFetch<Budget[]>(`/budgets${planId ? `?plan_id=${planId}` : ''}`),
  listCashFlow: (planId?: string) => finFetch<CashFlowPlan[]>(`/cashflow${planId ? `?plan_id=${planId}` : ''}`),
  listProfitability: (planId?: string) => finFetch<ProfitabilityModel[]>(`/profitability${planId ? `?plan_id=${planId}` : ''}`),
  getWorkingCapital: (planId?: string) => finFetch<WorkingCapitalModel>(`/working-capital${planId ? `?plan_id=${planId}` : ''}`),
  listRisks: (planId?: string) => finFetch<FinancialRisk[]>(`/financial-risks${planId ? `?plan_id=${planId}` : ''}`),
  listRecommendations: (planId?: string) => finFetch<FinancialRecommendation[]>(`/financial-recommendations${planId ? `?plan_id=${planId}` : ''}`),
  listScenarios: (planId?: string) => finFetch<FinancialScenario[]>(`/financial-scenarios${planId ? `?plan_id=${planId}` : ''}`),
  approvePlan: (id: string) => finFetch<FinancialPlan>(`/financial-plans/${id}/approve`, { method: 'POST' }),
};
